// src/hooks/useAuth.jsx (enhanced expiry handling)
import { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const login = (token) => {
    localStorage.setItem('token', token);
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 > Date.now()) { // Validate on login too
        setUser(decoded);
        setError(null);
      } else {
        throw new Error('Token already expired');
      }
    } catch (err) {
      console.error('Login failed - invalid token:', err);
      setError('Invalid token');
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
  };

  // Enhanced: Strict expiry check on every app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 <= Date.now()) {
          console.warn('Token expired - auto-logout');
          logout(); // Clear and reset
        } else {
          setUser(decoded);
        }
      } catch (err) {
        console.error('Token parse error - auto-logout:', err);
        logout();
      }
    }
    setLoading(false);
  }, []); // Runs once on mount

  const value = { user, loading, error, login, logout, isAdmin: user?.role === 'admin' };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};